import React, { useMemo } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Button, 
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Brewery, DEFAULT_HERO_IMAGE } from '../../data/breweries';
import { useBeer } from '../../state/BeerContext';
import BeerBadgesRail from '../BeerBadgesRail/BeerBadgesRail';
import styles from './BreweryDetailPage.module.css';

interface BreweryDetailPageProps {
  brewery: Brewery;
  onClose: () => void;
  onFilterByBrewery: (breweryName: string) => void;
}

/**
 * BreweryDetailPage component - displays detailed information about a brewery
 * including location, links, and a list of their beers at the festival.
 */
const BreweryDetailPage: React.FC<BreweryDetailPageProps> = ({ 
  brewery, 
  onClose,
  onFilterByBrewery 
}) => {
  const { state, userInteractions } = useBeer();
  
  // Get all beers from this brewery
  const breweryBeers = useMemo(() => {
    return state.beers.filter(beer => beer.brewery === brewery.name);
  }, [state.beers, brewery.name]);
  
  // Count tried and favourited beers
  const triedCount = useMemo(() => {
    return breweryBeers.filter(beer => 
      (userInteractions.triedList || []).includes(beer.id) ||
      (userInteractions.ratings[beer.id] !== undefined && userInteractions.ratings[beer.id] > 0) ||
      (userInteractions.notes[beer.id] !== undefined && userInteractions.notes[beer.id].trim() !== '')
    ).length;
  }, [breweryBeers, userInteractions]);
  
  // Check if a beer is favourited
  const isBeerFavourite = (beerId: string): boolean => {
    return (userInteractions.favorites || []).includes(beerId);
  };
  
  // Get beer rating
  const getBeerRating = (beerId: string): number => {
    return userInteractions.ratings[beerId] || 0;
  };
  
  // Get beer notes
  const getBeerNotes = (beerId: string): string => {
    return userInteractions.notes[beerId] || '';
  };

  const hasMapCoordinates =
    Number.isFinite(brewery.location.latitude) &&
    Number.isFinite(brewery.location.longitude) &&
    !(brewery.location.latitude === 0 && brewery.location.longitude === 0);
  const hasMapAddress = Boolean(brewery.location.address?.trim() || brewery.location.postcode?.trim());
  const hasMapData = hasMapCoordinates || hasMapAddress;
  
  // Generate Google Maps URL for the brewery location
  const mapsUrl = hasMapCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${brewery.location.latitude},${brewery.location.longitude}`
    : undefined;
  
  const handleFilterClick = () => {
    onFilterByBrewery(brewery.name);
    onClose();
  };

  return (
    <Box className={styles.container} onClick={onClose}>
      <Box className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Hero Section */}
        <Box 
          className={styles.hero}
          style={{ backgroundImage: `url(${brewery.heroImageUrl || DEFAULT_HERO_IMAGE})` }}
        >
        {/* Close button - inside hero for proper positioning */}
        <IconButton 
          onClick={onClose} 
          className={styles.closeButton}
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: '1.9rem' }} />
        </IconButton>
        <Box className={styles.heroOverlay} />
        {brewery.logoUrl && (
          <Box className={styles.breweryLogo}>
            <img 
              src={brewery.logoUrl} 
              alt={`${brewery.name} logo`}
              className={styles.logoImage}
            />
          </Box>
        )}
        <Box className={styles.heroContent}>
          <Typography
            variant="h4"
            className={styles.heroTitle}
            style={{
              margin: 0,
              fontFamily: '"Work Sans", serif',
              fontWeight: 700,
              fontSize: '1.6rem',
              lineHeight: 1.2,
              paddingRight: 0,
              color: 'var(--primary-colour)',
            }}
          >
            {brewery.name}
          </Typography>
          <Box className={styles.heroMeta}>
            {brewery.established && (
              <Box className={styles.establishedBadge}>
                <CalendarTodayIcon className={styles.establishedIcon} />
                <span>Est. {brewery.established}</span>
              </Box>
            )}
            {hasMapData && (
              <Typography className={styles.heroLocation}>
                <LocationOnIcon className={styles.locationIcon} />
                {brewery.location.town}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      
      {/* Content */}
      <Box className={styles.content}>
        {/* Quick Stats */}
        <Box className={styles.statsRow}>
          <Box className={styles.statCard}>
            <LocalBarIcon className={styles.statIcon} />
            <Box className={styles.statInfo}>
              <span className={styles.statNumber}>{breweryBeers.length}</span>
              <span className={styles.statLabel}>Beers</span>
            </Box>
          </Box>
          <Box className={styles.statCard}>
            <CheckCircleIcon className={styles.statIconTried} />
            <Box className={styles.statInfo}>
              <span className={styles.statNumber}>{triedCount}</span>
              <span className={styles.statLabel}>Tried</span>
            </Box>
          </Box>
        </Box>
        
        {/* Description Card */}
        <Box className={styles.card}>
          <Typography className={styles.cardTitle}>About</Typography>
          <Typography className={styles.description}>
            {brewery.description}
          </Typography>
        </Box>
        
        {/* Action Buttons */}
        <Box className={styles.actionButtons}>
          {brewery.website && (
            <Button
              variant="contained"
              href={brewery.website}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.websiteButton}
              startIcon={<LanguageIcon />}
              fullWidth
            >
              Visit Website
            </Button>
          )}
          {brewery.shopUrl && (
            <Button
              variant="contained"
              href={brewery.shopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.shopButton}
              startIcon={<ShoppingCartIcon />}
              fullWidth
            >
              Buy Their Beer
            </Button>
          )}
          {hasMapData && mapsUrl && (
            <Button
              variant="outlined"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.mapButton}
              startIcon={<LocationOnIcon />}
              fullWidth
            >
              {brewery.location.address}, {brewery.location.postcode}
            </Button>
          )}
        </Box>
        
        {/* Beers Section */}
        <Box className={styles.beersSection}>
          <Box className={styles.beersSectionHeader}>
            <Typography className={styles.beersSectionTitle}>
              Festival Beers
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={handleFilterClick}
              endIcon={<FilterListIcon />}
              className={styles.filterButton}
              aria-label="Show all beers from this brewery"
            >
              Filter
            </Button>
          </Box>
          
          <Box className={styles.beerGrid}>
            {breweryBeers.map(beer => {
              const rating = getBeerRating(beer.id);
              const notes = getBeerNotes(beer.id);
              const isFavourite = isBeerFavourite(beer.id);
              const isInTastingList = (userInteractions.tastingList || []).includes(beer.id);
              
              return (
                <Box key={beer.id} className={styles.beerCard}>
                  <Box className={styles.beerCardContent}>
                    <Box className={styles.beerCardInfo}>
                      <Typography className={styles.beerName}>{beer.name}</Typography>
                      <Typography className={styles.beerStyle}>{beer.style}</Typography>
                      <Box className={styles.beerMeta}>
                        <Chip 
                          label={`${beer.abv}%`} 
                          size="small" 
                          className={styles.abvChip}
                        />
                      </Box>
                      {notes && (
                        <Box className={styles.beerNotes}>
                          <Typography className={styles.notesLabel}>Your notes:</Typography>
                          <Typography className={styles.notesText}>{notes}</Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Box className={styles.beerBadges}>
                      <BeerBadgesRail
                        variant="card"
                        availability={beer.availability}
                        isInTastingList={isInTastingList}
                        rating={rating}
                        isFavourite={isFavourite}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default BreweryDetailPage;
